/* eslint-disable react-hooks/exhaustive-deps */
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCountries, uploadFile } from '@eliadmin/core/src/api/general/general-api';
import { SignInFormCart } from '@eliadmin/core/src/components/auth/sign-in-form-cart';
import { CartWholesalerOrderForm } from '@eliadmin/core/src/components/cart/order/wholesaler/cart-wholesaler-order-form';
import FileDrop from '@eliadmin/core/src/components/form/file-drop';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import type { InputProps } from '@eliadmin/core/src/components/ui/input';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { UploadContext, type CreateOrderFormValues } from '@eliadmin/core/src/pages/cart/page';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useFormContext, type Control, type FieldPath } from 'react-hook-form';

export function CartOrderForm() {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const form = useFormContext<CreateOrderFormValues>();
  const generalLayout = useGlobalStore(s => s.generalLayout);
  const heureka_connected = !!generalLayout.marketing?.heureka_customer_verification_id;

  const { data: countries } = useSuspenseQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  const me = useCustomerStore(s => s.me);

  useEffect(() => {
    // Profile/draft hydration belongs to the parent useCartOrderDraft hook.
    // Preserve the existing backend password workaround without replacing input.
    if (!form.getValues('password')) {
      form.setValue('password', 'BACKEND_REQUIRES_PASSWORD_REMOVE_SOMEDAY_TODO');
    }
    if (!form.getValues('password_confirmation')) {
      form.setValue('password_confirmation', 'BACKEND_REQUIRES_PASSWORD_REMOVE_SOMEDAY_TODO');
    }
  }, [me]);

  const { attachments, setAttachments } = useContext(UploadContext);

  const handleFilesSelected = useCallback(
    (files: File[]) => {
      if (!files.length) return;

      const queuedAttachments = files.map(file => ({
        clientId: `${file.name}-${file.size}-${file.lastModified}-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2)}`,
        name: file.name,
        status: 'uploading' as const,
        file,
      }));

      setAttachments(current => [
        ...current,
        ...queuedAttachments.map(({ file: _file, ...attachment }) => attachment),
      ]);

      queuedAttachments.forEach(async attachment => {
        try {
          const formData = new FormData();
          formData.append('file', attachment.file);

          const res = await uploadFile({
            formData,
            locale,
          });

          setAttachments(current =>
            current.map(item =>
              item.clientId === attachment.clientId
                ? { ...item, id: res.data.id, status: 'uploaded', error: undefined }
                : item,
            ),
          );
        } catch (error) {
          setAttachments(current =>
            current.map(item =>
              item.clientId === attachment.clientId
                ? {
                    ...item,
                    status: 'error',
                    error: error instanceof Error ? error.message : 'Subor sa nepodarilo nahrat.',
                  }
                : item,
            ),
          );

          toast({
            title: t('text594'),
            description: `${attachment.name}: Subor sa nepodarilo nahrat.`,
            variant: 'destructive',
            duration: 100000,
          });
        }
      });
    },
    [locale, setAttachments, t],
  );

  const handleFileRemove = useCallback(
    (clientId: string) => {
      setAttachments(current => current.filter(attachment => attachment.clientId !== clientId));
    },
    [setAttachments],
  );

  const cid = form.watch('country2.id');
  const isCountrySK = form.watch('country.id') === 'SK';
  const companyBuy = form.watch('company_buy');
  const differentDeliveryAddress = form.watch('different_delivery_address');

  if (me?.customer?.is_wholesaler) {
    return (
      <div>
        {!me && <SignInFormCart />}
        <CartWholesalerOrderForm countries={countries} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {!me && <SignInFormCart />}
      <div className="rounded-lg border border-border p-4 md:p-8">
        {!me && <h3 className={cn('mb-4 text-start text-2xl font-bold')}>{t('text678')}</h3>}
        {/* <div>
          <h3 className={cn('mb-4 text-start text-lg font-bold')}>{t('text198')}</h3>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <Field
              control={form.control}
              name="name"
              label={t('text33')}
              placeholder={t('text33')}
              required
            />
            <Field
              control={form.control}
              name="surname"
              label={t('text34')}
              placeholder={t('text34')}
              required
            />
            <Field
              control={form.control}
              name="email"
              label={t('text35')}
              placeholder={t('text35')}
              required
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem required className={cn('', 'ea-register-form__phone-field')}>
                  <FormLabel>{t('text36')}</FormLabel>
                  <FormControl>
                    <PhoneInputField {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className="mt-4 flex flex-col">
          <h3
            className={cn(
              'mb-4 text-start text-lg font-bold',
              'ea-sign-in-form-cart__header-title',
            )}
          >
            {differentDeliveryAddress ? t('text679') : t('text359')}
          </h3>
          <div className="mb-2">
            <Field control={form.control} name="company_buy" type="checkbox" label={t('text361')} />
          </div>
          <div className="grid w-full grid-cols-1 gap-2 md:grid-cols-12 ">
            <div className="col-span-12 grid grid-cols-12">
              <div className="col-span-12 md:col-span-6">
                <Field
                  control={form.control}
                  name="address"
                  label={t('text38')}
                  placeholder={t('text38')}
                  required
                />
              </div>
              <div className="col-span-12 md:col-span-6">
                <Field
                  control={form.control}
                  name="street_number"
                  label={t('text438')}
                  placeholder={t('text438')}
                  required
                />
              </div>
              <div className="col-span-12 min-w-[80px] md:col-span-3">
                <Field
                  control={form.control}
                  name="postcode"
                  label={t('text40')}
                  placeholder={t('text40')}
                  required
                />
              </div>
              <div className="col-span-12 md:col-span-9">
                <Field
                  control={form.control}
                  name="city"
                  label={t('text39')}
                  placeholder={t('text39')}
                  required
                />
              </div>
              <div className="col-span-12">
                <Field
                  control={form.control}
                  name="country.id"
                  type="select"
                  isDisabled
                  label={t('text41')}
                  placeholder={t('text41')}
                  help={t('text603')}
                  options={countries.data.map(c => ({ value: c.id, label: t(c.name) }))}
                  required
                />
              </div>
            </div>
            <Collapsible open={differentDeliveryAddress} className="col-span-12 my-2">
              <CollapsibleTrigger asChild>
                <Field
                  control={form.control}
                  name="different_delivery_address"
                  type="checkbox"
                  label={t('text365')}
                />
              </CollapsibleTrigger>

              <CollapsibleContent className="ml-0 flex flex-col">
                <h3 className={cn('my-2 text-start text-lg font-bold')}>{t('text359')}</h3>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-12">
                  <div className="md:col-span-6">
                    <Field
                      control={form.control}
                      name="address2"
                      label={t('text38')}
                      placeholder={t('text38')}
                      required
                    />
                  </div>
                  <div className="md:col-span-6">
                    <Field
                      control={form.control}
                      name="street_number2"
                      label={t('text438')}
                      placeholder={t('text438')}
                      required
                    />
                  </div>
                  <div className="min-w-[80px] md:col-span-3">
                    <Field
                      control={form.control}
                      name="postcode2"
                      label={t('text40')}
                      placeholder={t('text40')}
                      required
                    />
                  </div>
                  <div className="md:col-span-9">
                    <Field
                      control={form.control}
                      name="city2"
                      label={t('text39')}
                      placeholder={t('text39')}
                      required
                    />
                  </div>
                  <div className="md:col-span-12">
                    <Field
                      control={form.control}
                      name="country2.id"
                      type="select"
                      label={t('text41')}
                      placeholder={t('text41')}
                      options={countries.data.map(c => ({ value: c.id, label: t(c.name) }))}
                      required
                    />
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
            <Collapsible open={companyBuy} className="col-span-12 mb-4 sm:mb-6">
              <CollapsibleContent className="ml-0 flex flex-col">
                <h3 className={cn('my-2 text-start text-lg font-bold')}>{t('text199')}</h3>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-12">
                  <div className="md:col-span-6">
                    <Field
                      control={form.control}
                      name="company"
                      label={t('text37')}
                      placeholder={t('text37')}
                    />
                  </div>
                  <div className="md:col-span-6">
                    <Field
                      control={form.control}
                      name="ico"
                      label={t('text42')}
                      placeholder={t('text42')}
                      required
                    />
                  </div>
                  <div className="md:col-span-6">
                    <Field
                      control={form.control}
                      name="dic"
                      label={t('text43')}
                      placeholder={t('text43')}
                    />
                  </div>
                  {isCountrySK && (
                    <div className="md:col-span-6">
                      <Field
                        control={form.control}
                        name="ic_dph"
                        label={t('text44')}
                        placeholder={t('text44')}
                        required={isCountrySK}
                      />
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div> */}
        <div className="col-span-12 flex flex-col gap-4">
          <Field
            control={form.control}
            name="external_order_number"
            type="text"
            label={t('text1120')}
          />

          <Field control={form.control} name="note" type="textarea" label={t('text57')} />
          <Separator />
          <Label>{t('text333')}</Label>
          <FileDrop
            onFilesSelected={handleFilesSelected}
            onFileRemove={handleFileRemove}
            selectedFiles={attachments}
            // Keep this in sync with the backend file upload validation.
            accept="image/jpeg, image/png, image/bmp, image/gif, image/svg+xml, image/webp, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, video/mp4, text/plain, .txt"
            showAcceptText={false}
          />
          <Separator />
          <Field
            control={form.control}
            name="deliver_a_set"
            type="checkbox"
            label={t('text1119')}
          />
          {/* {heureka_connected && (
            <div className="py-2 md:py-3">
              <Field
                control={form.control}
                name="send_heureka_mail"
                type="checkbox"
                label={t('text2015')}
              />
            </div>
          )} */}
          <Field
            control={form.control}
            name="gdpr"
            type="checkbox"
            label={t('text363') + ' ' + t('text364')}
            required
          />
          <Field control={form.control} name="newsletter" type="checkbox" label={t('text371')} />
        </div>
      </div>
    </div>
  );
}

type FieldProps = {
  control: Control<CreateOrderFormValues>;
  name: FieldPath<CreateOrderFormValues>;
  placeholder?: string;
  label?: string;
  required?: boolean;
  type?: InputProps['type'] | 'textarea';
  options?: { value: string | number; label: string }[];
  isFullWidth?: boolean;
  isDisabled?: boolean;
  after?: React.ReactNode;
  help?: React.ReactNode;
};

export function Field(props: FieldProps) {
  const { t } = useTranslations();
  const form = useFormContext<CreateOrderFormValues>();
  const content = useMemo(() => {
    if (props.type === 'select') {
      if (!props.options) {
        throw new Error('Select type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required}>
              {props.label && <FormLabel>{props.label}</FormLabel>}
              <FormControl>
                <Select
                  value={String(field.value)}
                  onValueChange={e => {
                    if (props.isDisabled) return;
                    field.onChange(e);
                  }}
                  disabled={props.isDisabled}
                  aria-disabled={props.isDisabled}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={props.placeholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {props.options?.map(o => (
                      <SelectItem key={o.value} value={String(o.value)}>
                        {t(o.label as any)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
              <p className="text-xs text-muted-foreground">{props.help}</p>
            </FormItem>
          )}
        />
      );
    }

    if (props.type === 'checkbox') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required}>
              <FormControl>
                <div className="flex w-full flex-row items-center gap-2">
                  <Checkbox
                    {...(field as any)}
                    checked={Boolean(field.value)}
                    onCheckedChange={checked => {
                      form.setValue(props.name, (checked === true) as any, {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      });
                    }}
                    disabled={props.isDisabled}
                    aria-disabled={props.isDisabled}
                  />
                  {props.label && <FormLabel>{props.label}</FormLabel>}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    if (props.type === 'textarea') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required}>
              {props.label && <FormLabel>{props.label}</FormLabel>}
              <FormControl>
                <div className="flex w-full flex-row items-center gap-2">
                  <Textarea
                    placeholder={props.placeholder}
                    {...(field as any)}
                    value={field.value ?? ''}
                    disabled={props.isDisabled}
                    aria-disabled={props.isDisabled}
                    rows={5}
                    className={{ wrapper: 'w-auto flex-1' }}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    return (
      <FormField
        control={props.control}
        name={props.name}
        render={({ field }) => (
          <FormItem required={props.required}>
            {props.label && <FormLabel>{props.label}</FormLabel>}
            <FormControl>
              <div className="flex w-full flex-row items-center gap-2">
                <Input
                  placeholder={props.placeholder}
                  type={props.type}
                  {...(field as any)}
                  value={field.value ?? ''}
                  disabled={props.isDisabled}
                  aria-disabled={props.isDisabled}
                  afterOuter={props.after}
                  // added margins because of active ring overflowing
                  className={{ wrapper: 'm-[1px] w-auto flex-1', main: 'mt-[1px]' }}
                />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }, [
    props.after,
    props.control,
    form,
    props.isDisabled,
    props.label,
    props.name,
    props.options,
    props.placeholder,
    props.required,
    props.type,
    props.help,
    t,
  ]);

  return content;
}
